import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { cn } from '@shared/lib';
import { useToastStore, type Toast } from './toastStore';

const toneStyles: Record<Toast['tone'], string> = {
  success: 'border-success/35 bg-[#0f1f17] text-fg',
  error: 'border-danger/35 bg-[#1f1010] text-fg',
  warning: 'border-warn/35 bg-[#1d1810] text-fg',
  info: 'border-line bg-card text-fg',
};

const toneAccent: Record<Toast['tone'], string> = {
  success: 'text-success',
  error: 'text-danger',
  warning: 'text-warn',
  info: 'text-muted',
};

const Glyph = ({ tone }: { tone: Toast['tone'] }) => {
  if (tone === 'success') {
    return (
      <svg className={cn('h-4 w-4', toneAccent.success)} viewBox="0 0 16 16" fill="none" aria-hidden>
        <path d="M3 8.5L6.5 12L13 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (tone === 'error') {
    return (
      <svg className={cn('h-4 w-4', toneAccent.error)} viewBox="0 0 16 16" fill="none" aria-hidden>
        <path d="M8 4.5v4M8 11.2v.1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    );
  }
  if (tone === 'warning') {
    return (
      <svg className={cn('h-4 w-4', toneAccent.warning)} viewBox="0 0 16 16" fill="none" aria-hidden>
        <path d="M8 2.2L14.6 13.2H1.4L8 2.2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M8 6.7v3M8 11.6v.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg className={cn('h-4 w-4', toneAccent.info)} viewBox="0 0 16 16" fill="none" aria-hidden>
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 7v4.2M8 4.7v.1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
};

function ToastItem({ toast }: { toast: Toast }) {
  const dismiss = useToastStore((s) => s.dismiss);

  useEffect(() => {
    if (!toast.duration) return;
    const timer = window.setTimeout(() => dismiss(toast.id), toast.duration);
    return () => window.clearTimeout(timer);
  }, [toast.id, toast.duration, dismiss]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -16, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -12, scale: 0.97 }}
      transition={{ duration: 0.22, ease: [0.2, 0.9, 0.2, 1] }}
      className={cn(
        // w-[min(...)] — на узких экранах (320–393px) фикс 380px вылезал за вьюпорт.
        'pointer-events-auto flex w-[min(380px,calc(100vw-24px))] items-start gap-3 rounded-card border px-5 py-4',
        'shadow-[0_18px_44px_-12px_rgba(0,0,0,0.7)]',
        toneStyles[toast.tone],
      )}
      role={toast.tone === 'error' ? 'alert' : 'status'}
      aria-live={toast.tone === 'error' ? 'assertive' : 'polite'}
    >
      <div className="mt-0.5 shrink-0">
        <Glyph tone={toast.tone} />
      </div>
      <div className="min-w-0 flex-1">
        {toast.title && (
          <div className="mb-1 text-[14px] font-semibold leading-tight tracking-[0.01em] text-fg">
            {toast.title}
          </div>
        )}
        <div className="text-[14px] leading-relaxed text-fg/80">{toast.message}</div>
      </div>
      <button
        type="button"
        onClick={() => dismiss(toast.id)}
        className="shrink-0 rounded-pill px-1.5 text-[12px] text-muted-2 transition-colors hover:text-fg"
        aria-label="Закрыть"
      >
        ✕
      </button>
    </motion.div>
  );
}

export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="pointer-events-none fixed left-1/2 top-6 z-[1500] flex -translate-x-1/2 flex-col items-center gap-2.5">
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} />
        ))}
      </AnimatePresence>
    </div>,
    document.body,
  );
}
